// COORDINATION ACCESS - what ONE partner may do on each coordination tab of
// ONE project (owner, 2026-10-15: "on the partner page, i don't see how to
// enable the coordination tabs for them, like shopdrawings, tracking ...").
//
// Tabs down the side, View / Add / Edit / Delete across. Every rule comes
// from the server with the grid (back/utils/partnerCoordGrants):
//   offered   which cells exist for a partner at all - the others are the
//             project team's whatever is stored, and are drawn as a dash
//             with the reason, never as a switch that would be refused;
//   opens     what each cell lets them do, as its tooltip;
//   defaults  what partners could do before this grid existed - a cell set
//             by hand is tagged "changed" and "Same as default" puts it back.
//
// Each switch saves on its own (PATCH /partner-admin/coord-grants) and the
// grid redraws from the server's answer, so what is on screen is what is
// stored. Read-only for anyone the server says may not change it (canEdit),
// with the reason said once above the grid.
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Skeleton, Switch, Tag, Tooltip, message } from "antd";
import { getCoordGrants, setCoordGrants } from "api/partners/coordinationGrantsApi";

const VERBS = ["view", "add", "edit", "delete"];
const VERB_LABEL = { view: "View", add: "Add", edit: "Edit", delete: "Delete" };
const ORDER = ["shopDrawings", "tracking", "areas", "technicalDocs", "sheetSearch", "rfi", "documentImport"];
const T = { muted: "#667085", line: "#EFEFEF" };

export function CoordinationGridTable({ grid, canEdit, busyCell, onChange, onReset }) {
  if (!grid) return null;
  const overrides = new Set(grid.overrides || []);
  const tabs = ORDER.filter((t) => grid.grants && grid.grants[t]);
  return (
    <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="coord-grid">
      <thead>
        <tr>
          <th style={{ textAlign: "left", padding: "6px 4px", borderBottom: `1px solid ${T.line}` }}>Tab</th>
          {VERBS.map((v) => (
            <th key={v} style={{ padding: "6px 4px", borderBottom: `1px solid ${T.line}`, width: 76 }}>{VERB_LABEL[v]}</th>
          ))}
          <th style={{ borderBottom: `1px solid ${T.line}`, width: 130 }} />
        </tr>
      </thead>
      <tbody>
        {tabs.map((tab) => {
          const offered = (grid.offered && grid.offered[tab]) || [];
          const changed = offered.some((v) => overrides.has(`${tab}.${v}`));
          return (
            <tr key={tab} data-testid={`coord-row-${tab}`}>
              <td style={{ padding: "6px 4px", borderBottom: `1px solid ${T.line}` }}>
                {(grid.labels && grid.labels[tab]) || tab}
                {changed ? <Tag color="orange" style={{ marginLeft: 6 }}>changed</Tag> : null}
              </td>
              {VERBS.map((verb) => {
                const cell = `${tab}.${verb}`;
                if (!offered.includes(verb)) {
                  return (
                    <td key={verb} style={{ textAlign: "center", color: T.muted, borderBottom: `1px solid ${T.line}` }}>
                      <Tooltip title="Stays with the project team - a partner cannot be given this">
                        <span data-testid={`coord-cell-${cell}`}>-</span>
                      </Tooltip>
                    </td>
                  );
                }
                const on = !!grid.grants[tab][verb];
                const what = (grid.opens && grid.opens[tab] && grid.opens[tab][verb]) || "";
                const dflt = !!(grid.defaults && grid.defaults[tab] && grid.defaults[tab][verb]);
                return (
                  <td key={verb} style={{ textAlign: "center", borderBottom: `1px solid ${T.line}` }}>
                    <Tooltip title={`${what}${overrides.has(cell) ? ` (default: ${dflt ? "on" : "off"})` : ""}`}>
                      <Switch
                        size="small"
                        checked={on}
                        disabled={!canEdit}
                        loading={busyCell === cell}
                        aria-label={`${(grid.labels && grid.labels[tab]) || tab} ${VERB_LABEL[verb]}`}
                        data-testid={`coord-cell-${cell}`}
                        onChange={(v) => onChange(tab, verb, v)}
                      />
                    </Tooltip>
                  </td>
                );
              })}
              <td style={{ textAlign: "right", borderBottom: `1px solid ${T.line}` }}>
                {canEdit && changed ? (
                  <Button size="small" type="link" onClick={() => onReset(tab)}>Same as default</Button>
                ) : null}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** One partner row's grid, loaded and saved by participant id. `initial`
 *  (the participants list already carries it) skips the first read. */
export default function CoordinationAccessGrid({ participantId, initial = null, canEdit: canEditHint, onSaved }) {
  const [grid, setGrid] = useState(initial);
  const [canEdit, setCanEdit] = useState(!!canEditHint);
  const [error, setError] = useState("");
  const [busyCell, setBusyCell] = useState("");

  const load = useCallback(() => {
    if (!participantId) return;
    setError("");
    getCoordGrants(participantId)
      .then((g) => { setGrid(g); setCanEdit(!!g.canEdit); })
      .catch((e) => setError(e.message || "Could not read the coordination access"));
  }, [participantId]);
  useEffect(() => {
    if (initial) { setGrid(initial); setCanEdit(!!canEditHint); } else load();
  }, [initial, canEditHint, load]);

  const send = async (changes, cell) => {
    setBusyCell(cell);
    try {
      const g = await setCoordGrants(participantId, changes);
      setGrid(g);
      message.success((g.changed || []).length ? "Saved" : "Nothing changed");
      if (onSaved) onSaved(g);
    } catch (e) {
      message.error(e.message || "Could not change the coordination access");
    } finally {
      setBusyCell("");
    }
  };
  const onChange = (tab, verb, v) => send({ [tab]: { [verb]: v } }, `${tab}.${verb}`);
  const onReset = (tab) => send({ [tab]: Object.fromEntries(((grid.offered || {})[tab] || []).map((v) => [v, null])) }, `${tab}.reset`);

  if (error) return <Alert type="error" showIcon message={error} />;
  if (!grid) return <Skeleton active paragraph={{ rows: 4 }} />;
  return (
    <div>
      {!canEdit ? (
        <Alert style={{ marginBottom: 8 }} type="info" showIcon
               message="Only an admin of your company can change what a partner may do on these tabs." />
      ) : null}
      <CoordinationGridTable grid={grid} canEdit={canEdit} busyCell={busyCell} onChange={onChange} onReset={onReset} />
      <div style={{ color: T.muted, fontSize: 12, marginTop: 8 }}>
        A partner only ever works on their own company&apos;s items, and what they propose waits for your review.
        Executives can read but never change anything, whatever is switched on.
      </div>
    </div>
  );
}
